/**
 * 认证服务：实现 authStore 的 `AuthApiPort` 并注入，提供登录/注册/刷新/登出/用户信息接口。
 *
 * 模块加载时即调用 `setAuthApi` 注入实现，使 T02 的 authStore 中 `login` / `refresh`
 * 动作真正联网。
 *
 * @module services/auth
 */
import {
  useAuthStore,
  setAuthApi,
  type AuthApiPort,
} from '@/store/authStore';
import type { LoginResult, User } from '@/types';
import { API } from '@/config/api';
import { request } from './http';

/** 刷新接口返回体（仅含令牌，不含用户）。 */
interface RefreshBody {
  token: string;
  refreshToken: string;
}

/** 认证 API 实现（满足 authStore 的 AuthApiPort 契约）。 */
const authApiImpl: AuthApiPort = {
  async login(email: string, password: string): Promise<LoginResult> {
    return request<LoginResult>({
      method: 'POST',
      url: API.auth.login,
      data: { email, password },
    });
  },

  async refresh(refreshToken: string): Promise<LoginResult> {
    const body = await request<RefreshBody>({
      method: 'POST',
      url: API.auth.refresh,
      data: { refreshToken },
    });
    // 刷新成功后补充当前用户信息，组装成 LoginResult。
    const user = await request<User>({ method: 'GET', url: API.auth.me });
    return { token: body.token, refreshToken: body.refreshToken, user };
  },
};

// 模块加载时注入认证实现，使 authStore 的 login/refresh 真正联网。
setAuthApi(authApiImpl);

/** 注册入参。 */
export interface RegisterInput {
  email: string;
  password: string;
  name?: string;
}

/** 注册新用户（返回登录结果，便于自动登录）。 */
export async function register(input: RegisterInput): Promise<LoginResult> {
  return request<LoginResult>({
    method: 'POST',
    url: API.auth.register,
    data: input,
  });
}

/** 邮箱 + 密码登录（同步写入 authStore 与 localStorage）。 */
export async function login(email: string, password: string): Promise<void> {
  await useAuthStore.getState().login(email, password);
}

/** 使用刷新令牌刷新会话。 */
export async function refresh(refreshToken: string): Promise<LoginResult> {
  return authApiImpl.refresh(refreshToken);
}

/** 退出登录（先通知后端，再清理本地会话）。 */
export async function logout(): Promise<void> {
  await request<void>({ method: 'POST', url: API.auth.logout }).catch(
    () => undefined,
  );
  useAuthStore.getState().logout();
}

/** 获取当前登录用户信息。 */
export async function getMe(): Promise<User> {
  return request<User>({ method: 'GET', url: API.auth.me });
}
